import axios from "vue-app/utils/axios.ts";
import type {
  ImportHistoryItem,
  ImportStatistics,
  ImportError,
  GetImportHistoryResponse,
  GetImportErrorsResponse,
  GetImportErrorsParams,
  GetImportStatisticsParams,
  ValidateAndSuggestMappingResponse,
} from "../modules/Import/Import.types.ts";
import type { ImportConfig } from "../modules/Import/features/ImportWizard/ImportWizard.helpers.ts";

export async function getImportHistory(): Promise<ImportHistoryItem[]> {
  const { data } = await axios.post<GetImportHistoryResponse>("/json/settings/getImportHistory");
  return data.data.dataImportHistory;
}

export async function getImportStatistics(params: GetImportStatisticsParams): Promise<ImportStatistics> {
  const { data } = await axios.get<ImportStatistics>("/json/settings/import/statistics", {
    params: {
      dataImportId: params.dataImportId,
    },
  });
  return data;
}

export async function getImportErrors(params: GetImportErrorsParams): Promise<ImportError[]> {
  const { data } = await axios.post<GetImportErrorsResponse>("/json/settings/get_single_history", {
    dataImportId: params.dataImportId,
  });
  return data.data.dataImportErrors;
}

export async function getBillingUsageType(): Promise<string> {
  const { data } = await axios.get<string>("/billing/usage-type");
  return data;
}

export async function getImportTags(): Promise<Array<{ tagId: string; tagName: string; isDeleted: boolean }>> {
  const { data } = await axios.get<{ tagList: Array<{ tagId: string; tagName: string; isDeleted: boolean }> }>(
    "/json/settings/import/tags"
  );
  return data.tagList.filter((tag) => !tag.isDeleted);
}

export async function addImportTag(tagName: string): Promise<{ tagId: string; tagName: string } | null> {
  const { data } = await axios.put<{ tagId: string; tagName: string } | null>("/tags", {
    tag_name: tagName,
  });
  return data;
}

export async function validateAndSuggestMapping(firstLines: string): Promise<ValidateAndSuggestMappingResponse> {
  const formData = new FormData();
  formData.append("firstLines", firstLines);

  try {
    const { data } = await axios.post<ValidateAndSuggestMappingResponse>(
      "/import/validate-and-suggest-mapping",
      formData
    );

    if (!data.success) {
      throw new Error("Validation failed");
    }

    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Failed to validate file");
  }
}

export async function uploadImportFile(
  file: File,
  checksum: string,
  previewRows: string[][],
  skipFirstLine: boolean,
  delimiter: string
): Promise<{ dataImportId: string; fileName: string }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("checksum", checksum);
  formData.append("aiProcessed", "1");

  const firstDataRowIndex = skipFirstLine ? 1 : 0;
  if (previewRows.length > firstDataRowIndex) {
    formData.append("firstLines", previewRows[firstDataRowIndex].join(delimiter));
  }

  try {
    const { data } = await axios.post<{ data: { status: string; mappingResponse: { dataImportId: string } } }>(
      "/settings/import_file",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (!data?.data) {
      throw new Error("Invalid response from server");
    }

    if (data.data.status !== "success") {
      throw new Error(data.data.mappingResponse?.dataImportId || "Upload failed");
    }

    if (!data.data.mappingResponse?.dataImportId) {
      throw new Error("No import ID received from server");
    }

    return {
      dataImportId: data.data.mappingResponse.dataImportId,
      fileName: file.name,
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Failed to upload file");
  }
}

export async function submitMapping(importConfig: ImportConfig): Promise<void> {
  try {
    await axios.post("/json/settings/submit_mapping", importConfig);
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Failed to submit mapping");
  }
}

export async function getCustomerCount(): Promise<number> {
  try {
    const { data } = await axios.get<string>("/customers/list/v2/count?type_id=0");
    return parseInt(data, 10) || 0;
  } catch (error) {
    return 0;
  }
}
